import React, { ReactElement, useState } from 'react';
import {
  useCreateDeveloperJobsiteAccessGrantMutation,
  useGetDevelopersQuery,
} from 'apollo/generated/client-operations';
import { FormOnSubmit, ModalForm } from 'components/form';
import { AlertService } from 'components/alertService';
import { PlusIcon } from 'components/icons';
import { getDevelopersOptions } from 'containers/jobsites/helpers/utils';
import { useIsMounted } from 'utils';
import { getGraphQLError } from 'utils/error';
import {
  AddDataSharingGrantFormData,
  getCreateInput,
  getDefaultValues,
  getFormInputs,
} from './AddDataSharingGrantModal.forms';

export type AddDataSharingGrantModalProps = {
  isOpen: boolean;
  jobsiteId: string;
  onCancel: () => void;
  onConfirm: () => void;
};

const consentNotice =
  'Sharing this site’s data requires written approval from its current client. ' +
  'By adding a grant you attest that a written data-sharing agreement exists; ' +
  'record its reference below.';

export function AddDataSharingGrantModal(
  props: AddDataSharingGrantModalProps,
): ReactElement {
  const { isOpen, jobsiteId, onCancel, onConfirm } = props;

  const isMounted = useIsMounted();
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const { data: developersData } = useGetDevelopersQuery({
    fetchPolicy: 'no-cache',
    skip: !isOpen,
  });
  const developersOptions = getDevelopersOptions(
    developersData?.getCurrentSession.user.developers.edges.map(
      ({ node }) => node,
    ),
  );

  const [createGrant] = useCreateDeveloperJobsiteAccessGrantMutation();

  const onSubmit: FormOnSubmit<AddDataSharingGrantFormData> = async (
    data,
    event,
  ): Promise<void> => {
    if (isSaving) return;
    setIsSaving(true);

    try {
      const input = getCreateInput(jobsiteId, data);
      await createGrant({ variables: { input } });
      onConfirm();
      AlertService.alert('success', 'Success', 'Data sharing grant added');
    } catch (error) {
      event.preventDefault();
      AlertService.alert(
        'danger',
        'Something went wrong!',
        getGraphQLError(error),
      );
    } finally {
      if (isMounted()) setIsSaving(false);
    }
  };

  const formInputs = React.useMemo(
    () => getFormInputs({ developersOptions }),
    [developersOptions],
  );
  const defaultValues = React.useMemo(() => getDefaultValues(), []);

  return (
    <ModalForm
      open={isOpen}
      setOpen={onCancel}
      title="Add data sharing grant"
      subtitle={consentNotice}
      inputs={formInputs}
      onSubmit={onSubmit}
      defaultValues={defaultValues}
      inputsContainerClassName="odin-grid odin-grid-cols-1 odin-gap-6"
      actionText="Add grant"
      actionIcon={PlusIcon}
      actionButtonWithSpinner={isSaving}
    />
  );
}
